import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { useApi } from '@/contexts/ApiContext';
import { useAuth } from '@/contexts/AuthContext';
import { SsoLoginRequest, SsoErrorCode } from '@/api/userApi';
import { NavRoutes } from '@/data/NavRoutes';
import { logException } from '@/helpers/exceptionHelper';

const SsoView: React.FC = (): JSX.Element => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { singleSignOnApi, setToken } = useApi();
  const { loggedIn } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingRedirect, setPendingRedirect] = useState(false);

  useEffect(() => {
    // If we are waiting for login after SSO, show spinner until loggedIn is true
    if (pendingRedirect && loggedIn) {
      navigate(NavRoutes.Trade);
      return;
    }
  }, [loggedIn, navigate, pendingRedirect]);

  useEffect(() => {
    if (loggedIn && !pendingRedirect) {
      navigate(NavRoutes.Trade);
      return;
    }

    if (!code) {
      setError('Invalid SSO link. No code provided.');
      setLoading(false);
      return;
    }

    // Attempt SSO login
    const attemptSsoLogin = async () => {
      try {
        setLoading(true);
        setError(null);

        const request = new SsoLoginRequest({ token: code });
        const response = await singleSignOnApi.login(request);

        if (response.errorCode === SsoErrorCode.Success && response.token) {
          setToken(response.token);
          setPendingRedirect(true);
          // keep spinner until loggedIn is true
        } else {
          // Handle different error cases
          switch (response.errorCode) {
            case SsoErrorCode.UserNotFound:
              setError('User not found. Please contact support.');
              break;
            case SsoErrorCode.TokenNotFound:
              setError('Invalid or expired SSO token. Please try again or contact support.');
              break;
            case SsoErrorCode.UnknownError:
            default:
              setError(response.errorMessage || 'An unknown error occurred. Please try again.');
              break;
          }
          setLoading(false);
        }
      } catch (err) {
        logException(err, 'SSO login failed');
        setError('Failed to process SSO login. Please try again or contact support.');
        setLoading(false);
      }
    };

    attemptSsoLogin();
  }, [code, loggedIn, navigate, singleSignOnApi, setToken, pendingRedirect]);

  const handleBackToLogin = () => {
    navigate(NavRoutes.Login);
  };

  if (loading || pendingRedirect) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          textAlign: 'center',
          padding: 2
        }}
      >
        <CircularProgress size={60} sx={{ marginBottom: 2 }} />
        <Typography variant="h5" component="h1" gutterBottom>
          Logging you in...
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please wait while we authenticate your account.
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          textAlign: 'center',
          padding: 2
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom color="primary">
          Login Failed
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ marginBottom: 3, maxWidth: 400 }}>
          {error}
        </Typography>
        <Button
          variant="contained"
          onClick={handleBackToLogin}
          color="primary"
        >
          Back to Login
        </Button>
      </Box>
    );
  }

  return null;
};

export default SsoView;
